import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';

import { EncodeHelper } from '../helpers/encode.helper';

@Injectable({ providedIn: 'root' })
export class FileService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private encodeHelper = inject(EncodeHelper);
  private translateService = inject(TranslateService);

  fetchBlob = (path: string): Observable<Blob> => {
    return this.http.get(path, {
      responseType: 'blob'
    });
  };

  downloadFile = async (url: string, defaultName: string) => {
    const response: HttpResponse<Blob> = await firstValueFrom(
      this.http.get(url, {
        observe: 'response',
        responseType: 'blob'
      })
    );

    const blob = response.body;
    if (!blob) {
      throw new Error('Response body is null.');
    }

    let finalName = defaultName;

    const disposition = response.headers.get('Content-Disposition');

    if (disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        finalName = matches[1].replace(/['"]/g, '');
      }
    }

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();

    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  openFileInViewer = async (path: string, name: string, options?: { canDownload: boolean }): Promise<void> => {
    const canDownload = options?.canDownload || false;

    const header = { alg: 'HS256', typ: 'JWT' };
    const payload = { path, name, canDownload };

    const encodedHeader = this.encodeHelper.base64UrlEncode(JSON.stringify(header));
    const encodedPayload = this.encodeHelper.base64UrlEncode(JSON.stringify(payload));

    const key = await this.encodeHelper.getSigningKey();
    const dataToSign = this.encodeHelper.encode(`${encodedHeader}.${encodedPayload}`);
    const signatureBuffer = await crypto.subtle.sign('HMAC', key, dataToSign);
    const encodedSignature = this.encodeHelper.bufferToBase64Url(signatureBuffer);

    const token = `${encodedHeader}.${encodedPayload}.${encodedSignature}`;

    const urlTree = this.router.createUrlTree([`/${this.translateService.instant('ROUTES.files')}`], {
      queryParams: { token }
    });

    const url = this.router.serializeUrl(urlTree);

    window.open(url, '_blank');
  };

  public async verifyAndDecodeToken(
    token: string
  ): Promise<{ path: string; name: string; canDownload: boolean } | null> {
    const [encodedHeader, encodedPayload, encodedSignature] = token.split('.');
    if (!encodedHeader || !encodedPayload || !encodedSignature) {
      throw new Error('Invalid token structure.');
    }

    const key = await this.encodeHelper.getVerificationKey();
    const dataToVerify = this.encodeHelper.encode(`${encodedHeader}.${encodedPayload}`);
    const signatureBuffer = this.encodeHelper.base64UrlToBuffer(encodedSignature);

    const isValid = await crypto.subtle.verify('HMAC', key, signatureBuffer, dataToVerify);
    if (!isValid) {
      throw new Error('Token signature is invalid.');
    }

    const payload = JSON.parse(this.encodeHelper.base64UrlDecode(encodedPayload));

    return { path: payload.path, name: payload.name, canDownload: payload.canDownload };
  }
}
